import { existsSync, readdirSync } from "node:fs";
import { readFile, writeFile, utimes } from "node:fs/promises";
import { join } from "node:path";
import { renderIndex, scanDirectory } from "@vortex-os/index-generator";
import type { Command, CommandInput } from "@vortex-os/slash-commands";
import type { ModuleContext } from "@vortex-os/core";

interface ReindexTarget {
  readonly dir: string;
  readonly title: string;
  readonly description: string;
  readonly privacy: "internal" | "personal";
  readonly recursive: boolean;
  readonly skipPrefixes: readonly string[];
  readonly skipFilenames: readonly string[];
}

const TARGETS: readonly ReindexTarget[] = [
  {
    dir: "_memory",
    title: "Memory",
    description:
      "Persistent memory entries shared across sessions — curated; loaded in two tiers.",
    privacy: "internal",
    recursive: false,
    skipPrefixes: [],
    skipFilenames: ["MEMORY.md"],
  },
  {
    dir: "worklog",
    title: "Worklog",
    description: "Daily work log. Structure: `YYYY/MM/YYYY-MM-DD-keyword.md`.",
    privacy: "internal",
    recursive: true,
    skipPrefixes: [],
    skipFilenames: [],
  },
  {
    dir: "decision-log",
    title: "Decision Log",
    description: "Decision records — why a choice was made over the alternatives.",
    privacy: "personal",
    recursive: false,
    skipPrefixes: ["_TEMPLATE"],
    skipFilenames: [],
  },
  {
    dir: "runbooks",
    title: "Runbooks",
    description: "Incident-response and routine-maintenance procedures; `last_tested` tracks freshness.",
    privacy: "internal",
    recursive: false,
    skipPrefixes: [],
    skipFilenames: [],
  },
  {
    dir: "hubs",
    title: "Hubs",
    description: "Topic landing pages — index pages that gather related material in one place.",
    privacy: "internal",
    recursive: false,
    skipPrefixes: [],
    skipFilenames: [],
  },
  {
    dir: "projects",
    title: "Projects",
    description: "Active and completed projects.",
    privacy: "internal",
    recursive: true,
    skipPrefixes: [],
    skipFilenames: [],
  },
  {
    dir: "reference",
    title: "Reference",
    description: "Reference material — rules, cheat-sheets, and other look-ups.",
    privacy: "internal",
    recursive: true,
    skipPrefixes: [],
    skipFilenames: [],
  },
  {
    dir: "reports",
    title: "Reports",
    description: "Shareable reports and write-ups.",
    privacy: "internal",
    recursive: true,
    skipPrefixes: [],
    skipFilenames: [],
  },
  {
    dir: "tools",
    title: "Tools",
    description: "Scripts and small tools the user keeps.",
    privacy: "internal",
    recursive: true,
    skipPrefixes: [],
    skipFilenames: [],
  },
  {
    dir: "personal-records",
    title: "Personal Records",
    description: "Personal life/admin records (credentials, employment, certs).",
    privacy: "personal",
    recursive: true,
    skipPrefixes: [],
    skipFilenames: [],
  },
  {
    dir: "inbox",
    title: "Inbox",
    description: "Unfiled scratch notes, ideas, and backlog.",
    privacy: "internal",
    recursive: true,
    skipPrefixes: [],
    skipFilenames: [],
  },
  {
    dir: "_templates",
    title: "Templates",
    description: "Reusable document templates.",
    privacy: "internal",
    recursive: true,
    skipPrefixes: [],
    skipFilenames: [],
  },
];

/**
 * Resolve a target dir under `dataDir`, matching case-INSENSITIVELY. Targets are
 * lowercase (`tools`, `personal-records`, …) but a user-created or imported vault
 * may capitalize them (`Tools/`, `Personal-Records/`). On a case-insensitive FS
 * (Windows/macOS) the exact join happens to match; on Linux it would not, so a
 * capitalized bucket would silently never get an `_INDEX.md`. Prefer the exact
 * path; else scan for a directory whose name lowercases to the target. Returns
 * the actual path, or null when no such dir exists.
 */
function resolveDataDir(dataDir: string, name: string): string | null {
  const exact = join(dataDir, name);
  if (existsSync(exact)) return exact;
  try {
    const lower = name.toLowerCase();
    // DETERMINISTIC: if a (degenerate) FS holds several dirs that differ only in
    // case, sort and take the first rather than trusting readdir order, so the
    // same dir is chosen every run.
    const matches = readdirSync(dataDir, { withFileTypes: true })
      .filter((e) => e.isDirectory() && e.name.toLowerCase() === lower)
      .map((e) => e.name)
      .sort();
    return matches.length > 0 ? join(dataDir, matches[0]) : null;
  } catch {
    return null;
  }
}

export interface ReindexResult {
  readonly dir: string;
  readonly status: "written" | "unchanged" | "missing";
  readonly entries: number;
  readonly bytes: number;
}

/**
 * `/reindex [dir]` — Regenerate `_INDEX.md` for one or all known data
 * directories. When called with no argument, processes all targets;
 * when called with a directory name, processes only that target.
 *
 * Returns a list of results so the host can summarize. Unchanged
 * indexes are reported as `unchanged` (no write performed); missing
 * directories are reported as `missing` (no write attempted).
 */
export const reindexCommand: Command<readonly ReindexResult[]> = {
  name: "reindex",
  description:
    "Regenerate _INDEX.md for any configured target directory (or all targets when called with no argument). Idempotent — unchanged indexes are not rewritten.",
  args: [
    {
      name: "dir",
      description: "Optional directory name. Omit to reindex all targets.",
    },
  ],
  handler: async (input: CommandInput): Promise<readonly ReindexResult[]> => {
    const requested = input.args.dir;
    const targets = requested
      ? TARGETS.filter((t) => t.dir === requested)
      : TARGETS;

    if (requested && targets.length === 0) {
      throw new Error(
        `Unknown reindex target: "${requested}". Known: ${TARGETS.map((t) => t.dir).join(", ")}`,
      );
    }

    const results: ReindexResult[] = [];
    for (const t of targets) {
      const dir = resolveDataDir(input.context.dataDir, t.dir);
      if (!dir) {
        results.push({ dir: t.dir, status: "missing", entries: 0, bytes: 0 });
        continue;
      }

      const entries = await scanDirectory(dir, {
        recursive: t.recursive,
        skipPrefixes: t.skipPrefixes,
        skipFilenames: t.skipFilenames,
      });
      const body = renderIndex({
        title: t.title,
        description: t.description,
        entries,
        privacy: t.privacy,
      });

      const target = join(dir, "_INDEX.md");
      let existing;
      try {
        existing = await readFile(target, "utf8");
      } catch {
        existing = undefined;
      }
      if (existing === body) {
        results.push({
          dir: t.dir,
          status: "unchanged",
          entries: entries.length,
          bytes: body.length,
        });
        continue;
      }

      await writeFile(target, body, "utf8");
      results.push({
        dir: t.dir,
        status: "written",
        entries: entries.length,
        bytes: body.length,
      });
    }
    return results;
  },
};

/**
 * Auto-reindex `_memory/_INDEX.md` at session start (gated by
 * `autoRecord.reindex`). Mechanical and idempotent: it rewrites the index only
 * when the rendered content changes, then refreshes the index file's mtime so
 * the stale flag — newest `_memory/*.md` mtime > `_INDEX.md` mtime, see
 * `scanMemoryTiers` — clears even when the content was unchanged (a memory's
 * BODY changed but its one-line index row did not). Touching identical content
 * changes only mtime, so there is no git churn. Best-effort: any error returns
 * `"error"` and never blocks session start.
 */
export async function autoReindexMemory(
  ctx: ModuleContext,
): Promise<ReindexResult["status"] | "error"> {
  try {
    const target = TARGETS.find((t) => t.dir === "_memory");
    if (!target) return "missing";
    const dir = join(ctx.dataDir, target.dir);
    if (!existsSync(dir)) return "missing";
    const entries = await scanDirectory(dir, {
      recursive: target.recursive,
      skipPrefixes: target.skipPrefixes,
      skipFilenames: target.skipFilenames,
    });
    const body = renderIndex({
      title: target.title,
      description: target.description,
      entries,
      privacy: target.privacy,
    });
    const indexPath = join(dir, "_INDEX.md");
    let existing: string | undefined;
    try {
      existing = await readFile(indexPath, "utf8");
    } catch {
      existing = undefined;
    }
    // Compare IGNORING the frontmatter `updated:` date. `renderIndex` stamps it
    // with today by default, so an unchanged listing on a new day would otherwise
    // re-render to different bytes and rewrite the file EVERY day — daily git
    // churn. We only rewrite when the actual listing changes (and let that write
    // carry today's date); an unchanged listing keeps its file (and its date) and
    // we just refresh the mtime below.
    const sameListing =
      existing !== undefined && stripIndexDate(existing) === stripIndexDate(body);
    let status: ReindexResult["status"];
    if (sameListing) {
      status = "unchanged";
    } else {
      await writeFile(indexPath, body, "utf8");
      status = "written";
    }
    // Refresh the index mtime so `memoryIndexStale` clears even when the listing
    // was unchanged (content identical → git sees no diff, only the mtime moves).
    if (existsSync(indexPath)) {
      const now = new Date();
      await utimes(indexPath, now, now);
    }
    return status;
  } catch {
    return "error";
  }
}

/**
 * Drop the volatile frontmatter `updated:` line so an index whose listing is
 * unchanged is not treated as different just because `renderIndex` stamped a new
 * date. Only the top-level frontmatter line starts with `updated: ` at column 0;
 * the per-entry "Updated" column lives inside `| … |` table rows, so it is left
 * intact (a changed entry date is a real content change worth recording).
 */
function stripIndexDate(body: string): string {
  return body.replace(/^updated: .*$/m, "updated:");
}
